import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from 'components/Modal'
import { DetectBlur } from 'containers/util-containers'
import { SalaryGroupForm } from './types'
import { Form } from 'components/Form'
import styled from 'styled-components'
import { Table } from 'components/Table'
import { useState } from 'react'
import { Icon } from '@ur-solutions/react-components'
import { Input, UserSelect } from 'components'
import { SelectMultipleUsers } from 'components/Select/SelectMultipleUsers'

interface FieldProps {
  alignLeft?: boolean
  alignRight?: boolean
  area?: string
}
export const Field = styled.div<FieldProps>`
  grid-area: ${props => props.area};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${props => props.alignLeft && 'align-items: flex-start;'}
  ${props => props.alignRight && 'align-items: flex-end;'}

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

const StyledForm = styled(Form)`
  gap: 0.5rem;
`

const DoubleField = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
`

const StyledTable = styled(Table)`
  max-height: 300px;
  overflow-y: auto;
`

interface EditSalaryGroupPromptProps {
  initialData?: SalaryGroupForm
  companyUsers: {
    id: string
    fullName: string
  }[]
  onSubmit: (data: SalaryGroupForm | null) => void
}

export const EditSalaryGroupPrompt: React.FC<EditSalaryGroupPromptProps> = ({
  initialData,
  companyUsers,
  onSubmit,
}) => {
  const { formValues, formChangeHandler, formValid } = useForm<SalaryGroupForm>(
    {
      values: initialData ?? {
        name: '',
        users: [],
        responsibleUsers: [],
      },
      config: {
        initAsInvalid: true,
      },
      validators: {
        name: () => {
          if (!formValues.name) {
            return translations.error.required
          }
          return null
        },
      },
    }
  )

  const [userSearch, setUserSearch] = useState('')

  const translations = useTranslate({
    label: {
      name: 'common.name',
      remove: 'common.remove',
      search: 'common.search',
      addUsers: 'common.add-users',
      responsibleUsers: 'common.responsible-users',
    },
    create: 'common.create-alt',
    edit: 'common.edit',
    update: 'common.update',
    users: 'common.users',

    error: {
      required: 'common.required',
    },
  })

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <RegularModal
        title={initialData ? translations.edit : translations.create}
        submitText={translations.update}
        submitDisabled={!formValid}
        onCancel={() => onSubmit(null)}
        onSubmit={() => onSubmit(formValues)}
        width="600px"
      >
        <StyledForm onSubmit={() => onSubmit(formValues)}>
          <Field>
            <label>{translations.label.name}</label>
            <Input
              value={formValues.name}
              onChange={formChangeHandler('name')}
              grayed
            />
          </Field>
          <Field>
            <label>{translations.label.responsibleUsers}</label>
            <SelectMultipleUsers
              selected={formValues.responsibleUsers}
              onChange={formChangeHandler('responsibleUsers')}
              grayed
            />
          </Field>
          <DoubleField>
            <Field>
              <label>{translations.label.addUsers}</label>
              <UserSelect
                value={null}
                exclude={formValues.users}
                onChange={val =>
                  val
                    ? formChangeHandler('users')(
                        formValues.users.includes(val)
                          ? formValues.users.filter(id => id !== val)
                          : [...formValues.users, val]
                      )
                    : void 0
                }
                grayed
              />
            </Field>

            <Field>
              <label>{translations.label.search}</label>
              <Input
                value={userSearch}
                onChange={val => setUserSearch(val)}
                grayed
                placeholder={translations.label.search}
              />
            </Field>
          </DoubleField>

          <StyledTable headerMargin noData={formValues.users.length === 0}>
            <thead>
              <tr>
                <th>{translations.label.name}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {companyUsers
                .filter(
                  user =>
                    formValues.users.includes(user.id) &&
                    user.fullName.includes(userSearch)
                )
                .map(user => (
                  <tr>
                    <td>{user.fullName}</td>
                    <td width="1px">
                      <Icon
                        icon="times"
                        cursor="pointer"
                        color="gray6"
                        hoverColor="red"
                        onClick={() =>
                          formChangeHandler('users')(
                            formValues.users.filter(id => id !== user.id)
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </StyledTable>
        </StyledForm>
      </RegularModal>
    </DetectBlur>
  )
}
